/**
 * CORE-1401, возврат QA 23.10 — критерий 5: «ссылка на документ об обработке
 * данных видна в чате и на экране согласия». На экране согласия ссылка была, в
 * открытом чате — ноль: гейт стоит ВМЕСТО чата и после согласия уходит, а с ним
 * уходила и единственная дорога к документу.
 *
 * Одна строка под полем ввода: «Ответы формирует ИИ · Уведомление об ИИ».
 * Место — то же, где такие строки держат все ИИ-чаты: она не уезжает с
 * прокруткой и не встаёт поперёк переписки, как встала бы плашка сверху. Вид —
 * тихий, как у повтора знакомства (TourReplayLink, CORE-1750): подпись без
 * рамки и заливки. «Тихая» не значит «бледная»: и подпись, и ссылка держат
 * 4.5:1 в обеих темах (токены CORE-1733), иначе это не ссылка, а декор.
 *
 * Первая половина строки — заодно и пометка о том, что ответы формирует ИИ:
 * юрист 07.10 назвал её требованием закона об ИИ отдельно от уведомления.
 *
 * Как открывается документ:
 * - веб — настоящая ссылка `<a href="/legal/ai-notice">` в новой вкладке. Так же
 *   открывает документы гейт регистрации (ConsentGate, CORE-1404): разговор и
 *   экран согласия остаются на месте, а виджету не нужно знать, как свернуться;
 * - натив — экран LegalDoc; `onBeforeOpen` даёт шторке виджета закрыться до
 *   перехода, иначе документ открылся бы под ней.
 */
import React from 'react';
import { Platform, StyleSheet, Text, View } from 'react-native';

import { useTheme } from '../../context/ThemeContext';
import { useI18n } from '../../i18n';
import { navigateTo } from '../../navigation/rootNavigation';
import { ACCENT_TEXT, ACCENT_TEXT_HEX } from '../../styles/accentText';

export const AI_NOTICE_DOC_ID = 'ai-notice' as const;
export const AI_NOTICE_PATH = `/legal/${AI_NOTICE_DOC_ID}`;

const isWeb = Platform.OS === 'web';

/**
 * Цвет ссылки на Уведомление — и здесь, и на экране согласия.
 *
 * Прежде ссылка согласия красилась `colors.link` (#3B82F6): на бежевой плите
 * гейта #F1DAC3 это 2.72:1 при 12 px. Акцентный текст CORE-1733 на той же плите
 * даёт 4.9:1 в светлой теме и 7.1:1 в тёмной. На вебе это CSS-переменная темы;
 * на нативе ACCENT_TEXT — прежний фирменный #E98245 (2.0:1 на бежевом), поэтому
 * там берётся пара из той же статьи по теме.
 */
export function aiNoticeLinkColor(isDark: boolean): string {
  if (isWeb) return ACCENT_TEXT;
  return isDark ? ACCENT_TEXT_HEX.dark : ACCENT_TEXT_HEX.light;
}

/** Свойства, которые делают текст ссылкой на Уведомление на этой платформе. */
export function aiNoticeLinkProps(onBeforeOpen?: () => void): object {
  if (isWeb) {
    return { href: AI_NOTICE_PATH, hrefAttrs: { target: '_blank', rel: 'noopener' } };
  }
  return {
    onPress: () => {
      onBeforeOpen?.();
      navigateTo('LegalDoc', { docId: AI_NOTICE_DOC_ID });
    },
  };
}

interface Props {
  /** Натив: закрыть шторку виджета до перехода на документ. */
  onBeforeOpen?: () => void;
  /**
   * Нативный тред лежит на тёмной картине с затемнением, а не на поверхности
   * темы: там и подпись, и ссылка светлые при любой теме.
   */
  onArtwork?: boolean;
}

export const AiNoticeLink: React.FC<Props> = ({ onBeforeOpen, onArtwork = false }) => {
  const { colors, isDark } = useTheme();
  const { t } = useI18n('chats');
  // На картине треда (затемнение 45 %) светлые места дают под строкой около
  // #6E5835: тёплый акцент там проседает до 3.2:1, белый держит 6.7:1, а
  // подпись в 82 % белого — 5.2:1. Ссылку отличает подчёркивание и жирность.
  const captionColor = onArtwork ? 'rgba(255, 255, 255, 0.82)' : colors.textSecondary;
  const linkColor = onArtwork ? '#FFFFFF' : aiNoticeLinkColor(isDark);
  const linkLabel = t('aiNotice.link');

  return (
    <View style={styles.row} testID="aiNoticeRow">
      <Text style={[styles.caption, { color: captionColor }]}>
        {t('aiNotice.caption')}
        {' · '}
        <Text
          testID="aiNoticeLink"
          accessibilityRole="link"
          accessibilityLabel={linkLabel}
          style={[styles.link, { color: linkColor }]}
          {...aiNoticeLinkProps(onBeforeOpen)}
        >
          {linkLabel}
        </Text>
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingTop: 4,
    paddingBottom: 6,
  },
  caption: {
    fontSize: 12,
    lineHeight: 16,
    textAlign: 'center',
  },
  link: {
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
});
